import React, { useEffect, useState } from 'react';
import { Save, RefreshCw } from 'lucide-react';
import './AdsConfig.css';

import { fetchAdConfig, updateAdConfig } from '../services/api.ts';

const AdsConfig: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [adsEnabled, setAdsEnabled] = useState(true);
  const [adFrequency, setAdFrequency] = useState(5);
  const [gracePeriod, setGracePeriod] = useState(10);
  const [nativeAdInterval, setNativeAdInterval] = useState(3);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdConfig();
      setAdsEnabled(data.ads_enabled);
      setAdFrequency(data.ad_frequency);
      setGracePeriod(data.grace_period);
      setNativeAdInterval(data.native_ad_interval ?? 3);
      setUpdatedAt(data.updated_at || null);
    } catch {
      setError('Failed to load ad configuration.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfig(); }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateAdConfig({ ads_enabled: adsEnabled, ad_frequency: adFrequency, grace_period: gracePeriod, native_ad_interval: nativeAdInterval });
      setSuccess('Configuration saved successfully.');
      loadConfig();
    } catch {
      setError('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="ads-config-page"><div className="loading-spinner">Loading...</div></div>;
  }

  return (
    <div className="ads-config-page">
      <div className="page-header">
        <h1>Ad Configuration</h1>
        <button className="btn-refresh" onClick={loadConfig} title="Refresh">
          <RefreshCw size={18} />
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="config-card">
        <div className="config-row">
          <div className="config-label">
            <h3>Ads Enabled</h3>
            <p>Enable or disable ads globally for all users.</p>
          </div>
          <div className="config-control">
            <label className="toggle-switch">
              <input type="checkbox" checked={adsEnabled} onChange={(e) => setAdsEnabled(e.target.checked)} />
              <span className="toggle-slider"></span>
            </label>
            <span className={`toggle-label ${adsEnabled ? 'on' : 'off'}`}>
              {adsEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>

        <div className="config-row">
          <div className="config-label">
            <h3>Ad Frequency</h3>
            <p>Number of user actions before showing an ad.</p>
          </div>
          <div className="config-control">
            <input
              type="number"
              min={1}
              value={adFrequency}
              onChange={(e) => setAdFrequency(Number(e.target.value))}
              className="config-input"
            />
            <span className="input-hint">actions</span>
          </div>
        </div>

        <div className="config-row">
          <div className="config-label">
            <h3>Grace Period</h3>
            <p>Number of free actions for new users before ads begin.</p>
          </div>
          <div className="config-control">
            <input
              type="number"
              min={0}
              value={gracePeriod}
              onChange={(e) => setGracePeriod(Number(e.target.value))}
              className="config-input"
            />
            <span className="input-hint">actions</span>
          </div>
        </div>

        <div className="config-row">
          <div className="config-label">
            <h3>Native Ad Interval</h3>
            <p>Number of list items between inline native ads on all pages.</p>
          </div>
          <div className="config-control">
            <input
              type="number"
              min={1}
              max={20}
              value={nativeAdInterval}
              onChange={(e) => setNativeAdInterval(Number(e.target.value))}
              className="config-input"
            />
            <span className="input-hint">items</span>
          </div>
        </div>

        <div className="config-footer">
          {updatedAt && (
            <span className="last-updated">Last updated: {new Date(updatedAt).toLocaleString()}</span>
          )}
          <button className="btn-save" onClick={handleSave} disabled={saving}>
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdsConfig;
